<template>
  <div class="kyc-gate">
    <div v-if="statusCheckPending" class="kyc-gate__progress">
      <div class="kyc-gate__progress-content">
        <div class="kyc-gate__spinner" />
        <p class="kyc-gate__progress-text">{{ $t("kyc.starting") }}</p>
      </div>
    </div>

    <div
      v-else-if="showAmlbotLinkPanel"
      class="kyc-gate__aml-embed"
    >
      <div class="kyc-gate__card kyc-gate__card--standalone">
        <div class="kyc-gate__header">
          <img
            src="@/assets/img/shild-icon.svg"
            alt=""
            class="kyc-gate__shield"
            aria-hidden="true"
          />
          <h2 class="kyc-gate__title">{{ $t("kyc.title") }}</h2>
        </div>
        <p class="kyc-gate__reason kyc-gate__reason--embed">
          {{ $t("kyc.completeInNewTab") }}
        </p>
        <a
          :href="amlbotLink"
          target="_blank"
          rel="noopener noreferrer"
          class="kyc-gate__aml-link"
        >
          {{ amlbotLink }}
        </a>
        <button
          type="button"
          class="kyc-gate__btn kyc-gate__btn--primary"
          @click="openAmlbotInNewTab"
        >
          {{ $t("kyc.openInNewTab") }}
        </button>
        <div v-if="verificationInProgress" class="kyc-gate__aml-waiting">
          <div class="kyc-gate__spinner kyc-gate__spinner--inline" />
          <p class="kyc-gate__progress-text">{{ $t("kyc.verificationInProgress") }}</p>
        </div>
      </div>
    </div>

    <div
      v-else-if="
        !showSumSubForm &&
        !showAmlbotModal &&
        !renderAmlbotModal &&
        !verificationInProgress &&
        !verificationFailed &&
        !startingVerification
      "
      class="kyc-gate__intro"
    >
      <!-- Non-functional background UI: tabs + currency list (behind popover) -->
      <div class="kyc-gate__bg-ui" aria-hidden="true">
        <h2 class="kyc-gate__bg-heading">{{ $t("payment.choose-wallet") }}</h2>
        <div class="kyc-gate__bg-tabs">
          <div class="kyc-gate__bg-tab">
            <img
              src="@/assets/img/tabs/exchangeTabBlack.svg"
              alt=""
              class="kyc-gate__bg-tab-icon"
            />
            <span>{{ $t("tabs.crypto") }}</span>
          </div>
          <div class="kyc-gate__bg-tab kyc-gate__bg-tab--active">
            <img
              src="@/assets/img/tabs/walletTabWhite.svg"
              alt=""
              class="kyc-gate__bg-tab-icon"
            />
            <span>{{ $t("tabs.wallet") }}</span>
          </div>
        </div>
        <div class="kyc-gate__bg-content">
          <div class="kyc-gate__bg-search">
            <input
              type="text"
              class="kyc-gate__bg-search-input"
              disabled
              placeholder="Search currencies"
            />
          </div>
          <div class="kyc-gate__bg-list">
            <div class="kyc-gate__bg-list-item">Bitcoin (BTC)</div>
            <div class="kyc-gate__bg-list-item">Ethereum (ETH)</div>
            <div class="kyc-gate__bg-list-item">USDT (ERC20)</div>
            <div class="kyc-gate__bg-list-item">Litecoin (LTC)</div>
            <div class="kyc-gate__bg-list-item">USD Coin (USDC)</div>
          </div>
        </div>
      </div>
      <!-- Verification card as popover overlay -->
      <div class="kyc-gate__popover-backdrop" aria-hidden="true" />
      <div class="kyc-gate__popover">
        <div class="kyc-gate__card">
          <div class="kyc-gate__header">
            <img
              src="@/assets/img/shild-icon.svg"
              alt=""
              class="kyc-gate__shield"
              aria-hidden="true"
            />
            <h2 class="kyc-gate__title">{{ $t("kyc.title") }}</h2>
          </div>
          <div class="kyc-gate__reason-wrap">
            <p class="kyc-gate__reason">{{ $t("kyc.reason") }}</p>
          </div>
          <ul class="kyc-gate__benefits">
            <li class="kyc-gate__benefit">
              {{ $t("kyc.oneTimeVerification") }}
            </li>
            <li class="kyc-gate__benefit">{{ $t("kyc.validForever") }}</li>
          </ul>
          <button
            type="button"
            class="kyc-gate__btn kyc-gate__btn--primary"
            :disabled="startingVerification"
            @click="startVerification"
          >
            <span v-if="startingVerification">{{ $t("kyc.starting") }}</span>
            <span v-else>{{ $t("kyc.startVerification") }}</span>
          </button>
        </div>
      </div>
    </div>

    <div
      v-else-if="
        (verificationInProgress || startingVerification) &&
        !showSumSubForm &&
        !showAmlbotLinkPanel &&
        !showAmlbotModal &&
        !renderAmlbotModal
      "
      class="kyc-gate__progress"
    >
      <div class="kyc-gate__progress-content">
        <div class="kyc-gate__spinner" />
        <p class="kyc-gate__progress-text">
          {{ verificationProgressText }}
        </p>
      </div>
    </div>

    <div v-else-if="verificationFailed" class="kyc-gate__failed">
      <p class="kyc-gate__failed-text">{{ $t("kyc.verificationFailed") }}</p>
      <button
        type="button"
        class="kyc-gate__btn kyc-gate__btn--primary"
        @click="retryVerification"
      >
        {{ $t("kyc.retry") }}
      </button>
    </div>

    <b-modal
      v-if="isSumsubProvider"
      v-model="showSumSubForm"
      size="xl"
      centered
      hide-footer
      no-close-on-backdrop
      class="kyc-gate__modal"
      @shown="onSumSubModalShown"
      @hidden="onSumSubModalHidden"
    >
      <template #modal-header>
        <h5 class="kyc-gate__modal-title">{{ $t("kyc.title") }}</h5>
        <button
          type="button"
          class="kyc-gate__modal-close"
          aria-label="Close"
          @click="closeSumSubModal"
        >
          <img src="@/assets/img/cross.svg" alt="" width="16" height="16" />
        </button>
      </template>
      <div id="sumsub-websdk-container" class="kyc-gate__sumsub-container" />
    </b-modal>

    <b-modal
      v-if="isAmlbotProvider && renderAmlbotModal"
      v-model="showAmlbotModal"
      size="xl"
      centered
      hide-footer
      no-close-on-backdrop
      no-fade
      lazy
      class="kyc-gate__modal kyc-gate__modal--amlbot"
      @hidden="onAmlbotModalHidden"
    >
      <template #modal-header>
        <h5 class="kyc-gate__modal-title">{{ $t("kyc.title") }}</h5>
        <button
          type="button"
          class="kyc-gate__modal-close"
          aria-label="Close"
          @click="closeAmlbotModal"
        >
          <img src="@/assets/img/cross.svg" alt="" width="16" height="16" />
        </button>
      </template>
      <div class="kyc-gate__aml-iframe-wrap">
        <div
          v-show="amlbotIframeLoading"
          class="kyc-gate__aml-iframe-loader"
          aria-live="polite"
          aria-busy="true"
          :aria-label="amlbotLoaderStatusText"
        >
          <div class="kyc-gate__progress-content">
            <div class="kyc-gate__spinner" />
            <p class="kyc-gate__progress-text">{{ amlbotLoaderStatusText }}</p>
          </div>
        </div>
        <iframe
          v-show="amlbotLink"
          :src="amlbotLink || 'about:blank'"
          class="kyc-gate__aml-iframe"
          :class="{ 'kyc-gate__aml-iframe--loading': amlbotIframeLoading }"
          title="KYC verification"
          :allow="iframeMediaAllow"
          allowfullscreen
          @load="onAmlbotIframeLoad"
          @error="onAmlbotIframeLoad"
        />
      </div>
    </b-modal>
  </div>
</template>

<script>
import snsWebSdk from "@sumsub/websdk";
import { GET_SUMSUB_OBTAIN } from "@/store/modules/user/consts";
import {
  fetchKycSession,
  fetchKycStatus,
  isKycVerificationPassed,
  isKycVerificationRejected,
} from "@/utils/kycApi";
import { isAmlbotKycProvider } from "@/utils/kycProvider";
import helper from "@/utils/helper";
import {
  IFRAME_MEDIA_ALLOW,
  applyMediaAllowToIframes,
  requestEmbedderMediaPermissions,
} from "@/utils/iframePermissions";

const POLL_INTERVAL_MS = 4000;
const AMLBOT_LOADER_STATUS_INTERVAL_MS = 2500;
const AMLBOT_LOADER_STATUS_KEYS = [
  "kyc.amlbotLoader.preparingSession",
  "kyc.amlbotLoader.loadingScreen",
  "kyc.amlbotLoader.connectingProvider",
];

export default {
  name: "KycVerificationRequired",
  props: {
    isGuestUser: { type: Boolean, default: false },
  },
  data() {
    return {
      showSumSubForm: false,
      showAmlbotModal: false,
      renderAmlbotModal: false,
      amlbotLink: null,
      amlbotIframeLoading: false,
      amlbotLoaderStatusStep: 0,
      amlbotLoaderStatusTimer: null,
      statusCheckPending: true,
      verificationInProgress: false,
      verificationFailed: false,
      startingVerification: false,
      sumsubInstance: null,
      pollTimer: null,
      authWaitTimer: null,
      sumsubIframeObserver: null,
    };
  },
  computed: {
    canCheckKycStatus() {
      return !this.isGuestUser && this.$store.getters["auth/isAuth"];
    },
    isInIframe() {
      return helper.isInIframe();
    },
    isAmlbotProvider() {
      return isAmlbotKycProvider();
    },
    isSumsubProvider() {
      return !this.isAmlbotProvider;
    },
    iframeMediaAllow() {
      return IFRAME_MEDIA_ALLOW;
    },
    showAmlbotLinkPanel() {
      return (
        this.isAmlbotProvider &&
        this.isInIframe &&
        Boolean(this.amlbotLink) &&
        !this.verificationFailed
      );
    },
    verificationProgressText() {
      return this.$t("kyc.verificationInProgress");
    },
    amlbotLoaderStatusText() {
      const step = Math.min(
        this.amlbotLoaderStatusStep,
        AMLBOT_LOADER_STATUS_KEYS.length - 1
      );
      return this.$t(AMLBOT_LOADER_STATUS_KEYS[step]);
    },
  },
  async mounted() {
    await this.initializeKycGate();
    this.authWaitTimer = setTimeout(() => {
      if (this.statusCheckPending) {
        this.statusCheckPending = false;
      }
    }, 10000);
  },
  watch: {
    canCheckKycStatus: {
      async handler(ready) {
        if (!ready) {
          return;
        }
        this.clearAuthWaitTimer();
        await this.checkExistingStatus();
        this.statusCheckPending = false;
      },
    },
  },
  beforeUnmount() {
    this.stopPolling();
    this.stopAmlbotLoaderStatusCycle();
    this.stopSumsubIframePermissionObserver();
    if (this.authWaitTimer) {
      clearTimeout(this.authWaitTimer);
      this.authWaitTimer = null;
    }
    if (
      this.sumsubInstance &&
      typeof this.sumsubInstance.destroy === "function"
    ) {
      this.sumsubInstance.destroy();
    }
  },
  methods: {
    clearAuthWaitTimer() {
      if (this.authWaitTimer) {
        clearTimeout(this.authWaitTimer);
        this.authWaitTimer = null;
      }
    },
    async initializeKycGate() {
      if (this.isGuestUser) {
        this.clearAuthWaitTimer();
        this.statusCheckPending = false;
        return;
      }
      if (this.canCheckKycStatus) {
        await this.checkExistingStatus();
        this.clearAuthWaitTimer();
        this.statusCheckPending = false;
        return;
      }
      // Iframe postMessage: auth may land shortly after mount — keep spinner until ready.
    },
    async checkExistingStatus() {
      if (!this.canCheckKycStatus) {
        return;
      }
      try {
        const data = await fetchKycStatus(this.$store);
        if (isKycVerificationPassed(data)) {
          this.$emit("verified");
        }
      } catch {
        // No applicant or not verified yet
      }
    },
    async startVerification() {
      this.startingVerification = true;
      this.verificationFailed = false;
      try {
        try {
          const statusData = await fetchKycStatus(this.$store);
          if (isKycVerificationPassed(statusData)) {
            this.$emit("verified");
            return;
          }
        } catch {
          // No applicant or status API error – proceed to create/obtain
        }

        const session = await fetchKycSession(this.$store);
        if (!session) {
          this.verificationFailed = true;
          return;
        }

        if (this.isAmlbotProvider) {
          await this.launchAmlbot(session);
        } else {
          if (helper.isInIframe()) {
            requestEmbedderMediaPermissions();
          }
          await this.launchSumSub(session);
        }
      } catch (e) {
        console.error("KYC start error", e);
        this.verificationFailed = true;
      } finally {
        this.startingVerification = false;
      }
    },
    async launchAmlbot(session) {
      const link = typeof session === "object" ? session?.link : null;
      if (!link) {
        this.verificationFailed = true;
        return;
      }

      this.amlbotLink = link;
      this.verificationInProgress = true;
      this.startPolling();

      if (this.isInIframe) {
        return;
      }

      this.renderAmlbotModal = true;
      this.$nextTick(() => {
        this.amlbotIframeLoading = true;
        this.startAmlbotLoaderStatusCycle();
        this.showAmlbotModal = true;
      });
    },
    startAmlbotLoaderStatusCycle() {
      this.stopAmlbotLoaderStatusCycle();
      this.amlbotLoaderStatusStep = 0;
      this.amlbotLoaderStatusTimer = setInterval(() => {
        if (!this.showAmlbotModal) {
          return;
        }
        if (
          this.amlbotLoaderStatusStep <
          AMLBOT_LOADER_STATUS_KEYS.length - 1
        ) {
          this.amlbotLoaderStatusStep += 1;
        }
      }, AMLBOT_LOADER_STATUS_INTERVAL_MS);
    },
    stopAmlbotLoaderStatusCycle() {
      if (this.amlbotLoaderStatusTimer) {
        clearInterval(this.amlbotLoaderStatusTimer);
        this.amlbotLoaderStatusTimer = null;
      }
      this.amlbotLoaderStatusStep = 0;
    },
    onAmlbotIframeLoad() {
      if (!this.showAmlbotModal) {
        return;
      }
      this.amlbotIframeLoading = false;
      this.stopAmlbotLoaderStatusCycle();
    },
    openAmlbotInNewTab() {
      if (!this.amlbotLink) {
        return;
      }
      window.open(this.amlbotLink, "_blank", "noopener,noreferrer");
    },
    closeAmlbotModal() {
      this.stopAmlbotLoaderStatusCycle();
      this.showAmlbotModal = false;
    },
    onAmlbotModalHidden() {
      this.stopPolling();
      this.stopAmlbotLoaderStatusCycle();
      window.setTimeout(() => {
        this.resetAmlbotSessionState();
        this.verificationInProgress = false;
      }, 0);
    },
    resetAmlbotSessionState() {
      this.amlbotLink = null;
      this.amlbotIframeLoading = false;
      this.showAmlbotModal = false;
      this.renderAmlbotModal = false;
    },
    clearAmlbotSession() {
      this.stopAmlbotLoaderStatusCycle();
      if (this.showAmlbotModal || this.renderAmlbotModal) {
        this.showAmlbotModal = false;
        return;
      }
      this.resetAmlbotSessionState();
    },
    async launchSumSub(accessToken) {
      const getNewToken = () =>
        this.$store.dispatch(`user/${GET_SUMSUB_OBTAIN}`).catch(() => null);
      const config = {
        lang: (this.$i18n?.locale || "en").replace("-", "_"),
        theme: "light",
      };
      const options = {
        addViewportTag: false,
        adaptIframeHeight: true,
      };
      try {
        this.sumsubInstance = snsWebSdk
          .init(accessToken, getNewToken)
          .withConf(config)
          .withOptions(options)
          .on("idCheck.onStepCompleted", () => {})
          .on("idCheck.onError", (err) => {
            console.error("SumSub idCheck error", err);
            this.stopPolling();
            this.verificationInProgress = false;
            this.showSumSubForm = false;
            this.verificationFailed = true;
          })
          .onMessage((type, payload) => {
            if (type === "idCheck.applicantStatus" && payload?.reviewStatus) {
              if (payload.reviewStatus === "completed") {
                this.stopPolling();
                this.verificationInProgress = false;
                this.showSumSubForm = false;
                this.$emit("verified");
              } else if (
                payload.reviewStatus === "rejected" ||
                payload.reviewResult?.reviewAnswer === "RED"
              ) {
                this.stopPolling();
                this.verificationInProgress = false;
                this.showSumSubForm = false;
                this.verificationFailed = true;
              }
            }
          })
          .build();
        this.showSumSubForm = true;
      } catch (e) {
        this.stopPolling();
        this.verificationInProgress = false;
        this.showSumSubForm = false;
        this.verificationFailed = true;
        throw e;
      }
    },
    ensureSumsubIframePermissions() {
      const container = document.getElementById("sumsub-websdk-container");
      if (container) {
        applyMediaAllowToIframes(container);
      }
    },
    startSumsubIframePermissionObserver() {
      this.stopSumsubIframePermissionObserver();
      const container = document.getElementById("sumsub-websdk-container");
      if (!container) {
        return;
      }
      this.sumsubIframeObserver = new MutationObserver(() => {
        this.ensureSumsubIframePermissions();
      });
      this.sumsubIframeObserver.observe(container, {
        childList: true,
        subtree: true,
      });
    },
    stopSumsubIframePermissionObserver() {
      if (this.sumsubIframeObserver) {
        this.sumsubIframeObserver.disconnect();
        this.sumsubIframeObserver = null;
      }
    },
    onSumSubModalShown() {
      this.$nextTick(() => {
        if (this.sumsubInstance) {
          this.sumsubInstance.launch("#sumsub-websdk-container");
          this.ensureSumsubIframePermissions();
          this.startSumsubIframePermissionObserver();
          this.verificationInProgress = true;
          this.startPolling();
        }
      });
    },
    onSumSubModalHidden() {
      this.stopPolling();
      this.stopSumsubIframePermissionObserver();
      this.verificationInProgress = false;
      if (
        this.sumsubInstance &&
        typeof this.sumsubInstance.destroy === "function"
      ) {
        this.sumsubInstance.destroy();
        this.sumsubInstance = null;
      }
    },
    closeSumSubModal() {
      this.showSumSubForm = false;
    },
    startPolling() {
      this.stopPolling();
      this.pollTimer = setInterval(async () => {
        try {
          const data = await fetchKycStatus(this.$store);
          if (isKycVerificationPassed(data)) {
            this.stopPolling();
            this.verificationInProgress = false;
            this.showSumSubForm = false;
            this.clearAmlbotSession();
            this.$emit("verified");
          } else if (isKycVerificationRejected(data)) {
            this.stopPolling();
            this.verificationInProgress = false;
            this.showSumSubForm = false;
            this.clearAmlbotSession();
            this.verificationFailed = true;
          }
        } catch {
          // keep polling
        }
      }, POLL_INTERVAL_MS);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    retryVerification() {
      this.stopPolling();
      this.verificationFailed = false;
      this.showSumSubForm = false;
      this.clearAmlbotSession();
      this.verificationInProgress = false;
      if (
        this.sumsubInstance &&
        typeof this.sumsubInstance.destroy === "function"
      ) {
        this.sumsubInstance.destroy();
        this.sumsubInstance = null;
      }
      this.startVerification();
    },
  },
};
</script>

<style lang="scss" scoped>
.kyc-gate {
  padding: 0;
  margin-bottom: 40px;

  &__intro {
    position: relative;
    width: 100%;
    min-height: 364px;
  }

  &__bg-ui {
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    opacity: 0.4;
  }

  &__bg-heading {
    font-size: 16px;
    font-weight: 600;
    color: #111827;
    margin: 0 0 12px;
  }

  &__bg-tabs {
    display: flex;
    flex-direction: row;
    gap: 0;
    width: 100%;
    border-bottom: 1px solid #e5e7eb;
    margin-bottom: 12px;
  }

  &__bg-tab {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px 6px;
    font-size: 13px;
    color: #6b7280;
    border: none;
    background: transparent;

    &--active {
      color: #fff;
      background: #5559fe;
      border-radius: 6px 6px 0 0;
    }
  }

  &__bg-tab-icon {
    width: 18px;
    height: 18px;
    object-fit: contain;
  }

  &__bg-content {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 10px;
    background: #fafafa;
  }

  &__bg-search {
    margin-bottom: 10px;
  }

  &__bg-search-input {
    width: 100%;
    padding: 8px 10px;
    font-size: 13px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #fff;
    color: #9ca3af;
  }

  &__bg-list {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  &__bg-list-item {
    padding: 10px 8px;
    font-size: 13px;
    color: #374151;
    border-bottom: 1px solid #e5e7eb;

    &:last-child {
      border-bottom: none;
    }
  }

  &__popover-backdrop {
    position: absolute;
    inset: 0;
    z-index: 1;
    background: rgba(0, 0, 0, 0.15);
    pointer-events: none;
  }

  &__popover {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
    width: 100%;
    max-width: 420px;
  }

  &__card {
    background: #fff5f0;
    border: 1px solid #e85d2c;
    border-radius: 10px;
    padding: 14px;
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 0;
    margin: 0 0 8px;
    text-align: left;
  }

  &__shield {
    flex-shrink: 0;
    width: 18px;
    height: 21px;
    filter: invert(48%) sepia(79%) saturate(1200%) hue-rotate(350deg)
      brightness(95%) contrast(92%);
  }

  &__title {
    flex: 1;
    font-size: 14px;
    font-weight: 600;
    color: #b33a14;
    margin: 0;
  }

  &__reason-wrap {
    margin-bottom: 10px;
  }

  &__reason {
    font-size: 13px;
    line-height: 1.45;
    color: #8b4512;
    margin: 0 0 0 26px;
    padding: 0;
  }

  &__benefits {
    list-style: none;
    margin: 0 0 16px 26px;
    padding: 0;
  }

  &__benefit {
    position: relative;
    font-size: 13px;
    line-height: 1.45;
    color: #15803d;
    margin-bottom: 2px;
    padding-left: 14px;

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 5px;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #15803d;
    }
  }

  &__btn {
    min-width: 120px;
    display: block;
    margin: 12px auto 0;
    padding: 6px 10px;
    font-size: 13px;
    font-weight: 600;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }

    &--primary {
      background: #e85d2c;
      color: #fff;

      &:hover:not(:disabled) {
        background: #c94a1f;
      }
    }
  }

  &__progress {
    padding: 24px;
    text-align: center;
  }

  &__progress-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }

  &__spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top-color: #5559fe;
    border-radius: 50%;
    animation: kyc-spin 0.8s linear infinite;
  }

  &__progress-text {
    font-size: 14px;
    color: #4b5563;
    margin: 0;
  }

  &__failed {
    padding: 16px;
    text-align: center;
  }

  &__failed-text {
    font-size: 14px;
    color: #dc2626;
    margin: 0 0 16px;
  }

  &__sumsub-container {
    min-height: 400px;
    width: 100%;
  }

  &__aml-embed {
    padding: 8px 0 24px;
  }

  &__card--standalone {
    max-width: 420px;
    margin: 0 auto;
  }

  &__reason--embed {
    margin: 0 0 12px;
    padding: 0;
  }

  &__aml-link {
    display: block;
    margin: 0 0 14px;
    padding: 10px 12px;
    font-size: 12px;
    line-height: 1.4;
    word-break: break-all;
    color: #1d4ed8;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  &__aml-waiting {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    margin-top: 16px;
  }

  &__spinner--inline {
    width: 28px;
    height: 28px;
  }

  &__aml-iframe-wrap {
    position: relative;
    min-height: 520px;
  }

  &__aml-iframe-loader {
    position: absolute;
    inset: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: #fff;
    border-radius: 8px;

    .kyc-gate__progress-text {
      max-width: 280px;
      text-align: center;
    }
  }

  &__aml-iframe {
    display: block;
    width: 100%;
    min-height: 520px;
    border: 0;
    border-radius: 8px;
    background: #fff;

    &--loading {
      visibility: hidden;
    }
  }

  &__modal-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
  }

  &__modal-close {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px;
    margin: -6px -6px -6px auto;
    background: none;
    border: none;
    cursor: pointer;
    opacity: 0.7;
    line-height: 1;

    &:hover {
      opacity: 1;
    }
  }
}

:deep(.kyc-gate__modal .modal-header) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding-right: 8px;
}

:deep(.kyc-gate__modal .modal-body) {
  padding: 16px 0 0;
  min-height: 450px;
}

:deep(.kyc-gate__modal--amlbot .modal-body) {
  padding: 0;
  min-height: 540px;
}

@keyframes kyc-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
